import { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ClipboardPenLine,
  FileSignature,
  GraduationCap,
  MessageSquareText,
  Pencil,
  Plus,
  ShieldCheck,
  UserCog,
  UserPlus,
  UsersRound
} from 'lucide-react';
import PageHeader from '../../components/ui/PageHeader';
import { db, functions } from '../../core/firebase/firebase';
import { useAuth } from '../../core/auth/AuthProvider';
import TeachersPage from '../teachers/TeachersPage';
import TeacherRequestsAdmin from './TeacherRequestsAdmin';
import TeacherContractsAdmin from './TeacherContractsAdmin';

const VIEWS = {
  home: 'home',
  addTeacher: 'add-teacher',
  teachers: 'teachers',
  addStaff: 'add-staff',
  staff: 'staff',
  assignments: 'assignments',
  timetable: 'timetable',
  requests: 'requests',
  contracts: 'contracts'
};

const STAFF_ROLES = [
  { value: 'directeur', label: 'Directeur / Directrice' },
  { value: 'inspecteur', label: 'Inspecteur / Inspectrice' },
  { value: 'agent_ecole', label: 'Agent administratif' },
  { value: 'agent_college', label: 'Agent administratif collège' }
];

const EMPTY_STAFF = {
  fullname: '',
  username: '',
  phone: '',
  personalEmail: '',
  adminRole: 'agent_ecole',
  temporaryPassword: ''
};

function staffName(person) {
  return person?.fullname || person?.fullName || person?.username || person?.email || 'Membre du personnel';
}

function staffRoleLabel(role) {
  return STAFF_ROLES.find((item) => item.value === role)?.label || role || 'Personnel administratif';
}

function HubCard({ icon: Icon, title, description, action, onClick, disabled = false }) {
  return (
    <button type="button" className="personnel-hub-card" onClick={onClick} disabled={disabled}>
      <span className="personnel-hub-icon"><Icon size={24} /></span>
      <div className="personnel-hub-copy">
        <h2>{title}</h2>
        <p>{description}</p>
      </div>
      <span className="personnel-hub-action">{disabled ? 'Réservé à la gérance' : action}</span>
    </button>
  );
}

function StaffPanel({ mode }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(EMPTY_STAFF);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [editForm, setEditForm] = useState(EMPTY_STAFF);
  const [editSubmitting, setEditSubmitting] = useState(false);

  async function loadStaff() {
    setLoading(true);
    setError('');
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const items = snapshot.docs
        .map((item) => ({ id: item.id, ...item.data() }))
        .filter((item) => {
          const accountType = String(item.accountType || '').toLowerCase();
          const role = String(item.adminRole || item.role || '').toLowerCase();
          return accountType === 'administration' || STAFF_ROLES.some((candidate) => candidate.value === role);
        })
        .filter((item) => !['gerant', 'associe'].includes(String(item.adminRole || item.role || '').toLowerCase()))
        .sort((a, b) => staffName(a).localeCompare(staffName(b), 'fr'));
      setStaff(items);
    } catch (loadError) {
      console.error('Chargement personnel :', loadError);
      setError('Impossible de charger le personnel administratif.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadStaff();
  }, []);

  function updateField(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function submitStaff(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const createStaffAccount = httpsCallable(functions, 'createStaffAccount');
      const result = await createStaffAccount(form);
      setSuccess(`Le compte de ${result.data.fullname} a été créé avec succès.`);
      setForm(EMPTY_STAFF);
      await loadStaff();
    } catch (submitError) {
      console.error('Création personnel :', submitError);
      if (submitError.code === 'functions/already-exists') {
        setError('Cet identifiant est déjà utilisé.');
      } else if (submitError.code === 'functions/permission-denied') {
        setError('Cette action est réservée à la gérance.');
      } else {
        setError(submitError.message || 'Impossible de créer ce compte.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  function startEditing(person) {
    setEditingId(person.id);
    setEditForm({
      fullname: person.fullname || person.fullName || '',
      username: person.username || '',
      phone: person.phone || '',
      personalEmail: person.personalEmail || '',
      adminRole: person.adminRole || person.role || 'agent_ecole',
      temporaryPassword: ''
    });
    setError('');
    setSuccess('');
  }

  async function submitUpdate(event) {
    event.preventDefault();
    if (!editingId) return;
    setEditSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const updateStaffAccount = httpsCallable(functions, 'updateStaffAccount');
      await updateStaffAccount({ staffId: editingId, ...editForm });
      setSuccess('Les informations ont été mises à jour.');
      await loadStaff();
    } catch (submitError) {
      console.error('Modification personnel :', submitError);
      setError(submitError.message || 'Impossible de modifier ce membre du personnel.');
    } finally {
      setEditSubmitting(false);
    }
  }

  if (mode === 'add') {
    return (
      <section className="panel personnel-form-panel">
        <div className="personnel-section-heading">
          <span className="personnel-section-icon"><UserPlus size={22} /></span>
          <div>
            <p className="eyebrow">ÉQUIPE ADMINISTRATIVE</p>
            <h2>Ajouter un personnel administratif</h2>
            <p className="muted">Créez un accès et choisissez le rôle correspondant à sa fonction.</p>
          </div>
        </div>

        <form className="form-stack personnel-form" onSubmit={submitStaff}>
          <label>Nom et prénom<input name="fullname" value={form.fullname} onChange={updateField} required placeholder="Ex. Amel Trabelsi" /></label>
          <label>Fonction<select name="adminRole" value={form.adminRole} onChange={updateField} required>{STAFF_ROLES.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}</select></label>
          <label>Identifiant de connexion<input name="username" value={form.username} onChange={updateField} required placeholder="Ex. amel.trabelsi" /></label>
          <label>Téléphone<input name="phone" value={form.phone} onChange={updateField} placeholder="Ex. 22 000 000" /></label>
          <label>Email personnel<input type="email" name="personalEmail" value={form.personalEmail} onChange={updateField} placeholder="Facultatif" /></label>
          <label>Mot de passe temporaire<input type="text" name="temporaryPassword" value={form.temporaryPassword} onChange={updateField} minLength={8} required placeholder="Minimum 8 caractères" /></label>

          <div className="personnel-access-preview">
            <ShieldCheck size={18} />
            <div><strong>Accès administration</strong><span>{form.username ? form.username.trim().toLowerCase() : 'Identifiant à définir'} · {staffRoleLabel(form.adminRole)}</span></div>
          </div>

          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="success-note"><CheckCircle2 size={18} />{success}</div>}
          <button type="submit" className="button button-primary" disabled={submitting}>{submitting ? 'Création…' : 'Créer le compte'}</button>
        </form>
      </section>
    );
  }

  return (
    <section className="panel personnel-list-panel">
      <div className="personnel-section-heading personnel-list-heading">
        <span className="personnel-section-icon"><UsersRound size={22} /></span>
        <div>
          <p className="eyebrow">ÉQUIPE ADMINISTRATIVE</p>
          <h2>Personnel administratif</h2>
          <p className="muted">{loading ? 'Chargement…' : `${staff.length} membre${staff.length > 1 ? 's' : ''} enregistré${staff.length > 1 ? 's' : ''}.`}</p>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="success-note"><CheckCircle2 size={18} />{success}</div>}

      {!loading && !staff.length ? (
        <div className="compact-empty">Aucun personnel administratif enregistré.</div>
      ) : (
        <div className="personnel-directory">
          {staff.map((person) => (
            <article key={person.id} className={`personnel-person-card ${editingId === person.id ? 'is-editing' : ''}`}>
              <div className="personnel-person-main">
                <div className="personnel-avatar">{staffName(person).charAt(0).toUpperCase()}</div>
                <div className="personnel-person-copy">
                  <strong>{staffName(person)}</strong>
                  <span>{staffRoleLabel(person.adminRole || person.role)}</span>
                  <small>{person.phone || person.username || 'Coordonnées non renseignées'}</small>
                </div>
                <span className="personnel-status">{person.status === 'disabled' ? 'Désactivé' : 'Actif'}</span>
                <button type="button" className="button button-secondary personnel-edit-button" onClick={() => startEditing(person)}><Pencil size={16} />Modifier</button>
              </div>

              {editingId === person.id && (
                <form className="personnel-edit-form" onSubmit={submitUpdate}>
                  <div className="personnel-form-columns">
                    <label>Nom et prénom<input value={editForm.fullname} onChange={(event) => setEditForm((current) => ({ ...current, fullname: event.target.value }))} required /></label>
                    <label>Fonction<select value={editForm.adminRole} onChange={(event) => setEditForm((current) => ({ ...current, adminRole: event.target.value }))}>{STAFF_ROLES.map((role) => <option key={role.value} value={role.value}>{role.label}</option>)}</select></label>
                    <label>Identifiant<input value={editForm.username} onChange={(event) => setEditForm((current) => ({ ...current, username: event.target.value }))} required /></label>
                    <label>Téléphone<input value={editForm.phone} onChange={(event) => setEditForm((current) => ({ ...current, phone: event.target.value }))} /></label>
                    <label>Email personnel<input type="email" value={editForm.personalEmail} onChange={(event) => setEditForm((current) => ({ ...current, personalEmail: event.target.value }))} /></label>
                  </div>
                  <label>Nouveau mot de passe temporaire <span className="muted">(facultatif)</span><input type="text" minLength={8} value={editForm.temporaryPassword} onChange={(event) => setEditForm((current) => ({ ...current, temporaryPassword: event.target.value }))} placeholder="Laisser vide pour ne pas le changer" /></label>
                  <div className="personnel-edit-actions">
                    <button type="button" className="button button-secondary" onClick={() => setEditingId('')}>Annuler</button>
                    <button type="submit" className="button button-primary" disabled={editSubmitting}>{editSubmitting ? 'Enregistrement…' : 'Enregistrer les modifications'}</button>
                  </div>
                </form>
              )}
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

export default function EmployeesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { isGerant } = useAuth();
  const requestedView = searchParams.get('view');
  const view = Object.values(VIEWS).includes(requestedView) ? requestedView : VIEWS.home;

  const viewTitle = useMemo(
    () => ({
      [VIEWS.home]: 'Personnel',
      [VIEWS.addTeacher]: 'Ajouter un enseignant',
      [VIEWS.teachers]: 'Équipe pédagogique',
      [VIEWS.addStaff]: 'Ajouter un personnel administratif',
      [VIEWS.staff]: 'Personnel administratif',
      [VIEWS.assignments]: 'Affectations pédagogiques',
      [VIEWS.timetable]: 'Emploi du temps',
      [VIEWS.requests]: 'Demandes & communication',
      [VIEWS.contracts]: 'Contrats enseignants'
    })[view],
    [view]
  );

  function openView(nextView) {
    const next = new URLSearchParams(searchParams);
    next.set('view', nextView);
    setSearchParams(next);
  }

  function goHome() {
    const next = new URLSearchParams(searchParams);
    next.delete('view');
    setSearchParams(next);
  }

  if (view !== VIEWS.home) {
    return (
      <>
        <button type="button" className="personnel-back-button" onClick={goHome}>
          <ArrowLeft size={17} /> Retour à Personnel
        </button>

        <PageHeader
          eyebrow="PERSONNEL"
          title={viewTitle}
          description="Gérez l’équipe pédagogique et administrative depuis un espace centralisé."
        />

        {view === VIEWS.addTeacher && <TeachersPage mode="add" />}
        {view === VIEWS.teachers && <TeachersPage mode="list" />}
        {view === VIEWS.addStaff && <StaffPanel mode="add" />}
        {view === VIEWS.staff && <StaffPanel mode="list" />}
        {view === VIEWS.assignments && <TeachersPage mode="assignments" />}
        {view === VIEWS.timetable && <TeachersPage mode="timetable" />}
        {view === VIEWS.requests && <TeacherRequestsAdmin />}
        {view === VIEWS.contracts && <TeacherContractsAdmin />}
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow="MODULE"
        title="Personnel"
        description="Un seul espace pour gérer les enseignants, l’équipe administrative et l’organisation pédagogique."
      />

      <section className="personnel-hub-grid">
        <HubCard icon={UserPlus} title="Ajouter un enseignant" description="Créer un nouvel accès enseignant et enregistrer ses coordonnées." action="Ajouter" onClick={() => openView(VIEWS.addTeacher)} disabled={!isGerant} />
        <HubCard icon={UserCog} title="Équipe pédagogique" description="Consulter les enseignants et ouvrir leur dossier administratif complet." action="Voir l’équipe" onClick={() => openView(VIEWS.teachers)} disabled={!isGerant} />
        <HubCard icon={Plus} title="Ajouter un personnel administratif" description="Créer un compte pour un directeur, inspecteur ou agent administratif." action="Ajouter" onClick={() => openView(VIEWS.addStaff)} disabled={!isGerant} />
        <HubCard icon={UsersRound} title="Personnel administratif" description="Consulter et modifier les membres de l’équipe administrative." action="Voir l’équipe" onClick={() => openView(VIEWS.staff)} disabled={!isGerant} />
        <HubCard icon={ClipboardPenLine} title="Affectations pédagogiques" description="Attribuer les classes et les matières aux enseignants." action="Gérer les affectations" onClick={() => openView(VIEWS.assignments)} disabled={!isGerant} />
        <HubCard icon={CalendarDays} title="Emploi du temps" description="Créer les séances des enseignants à partir de leurs affectations." action="Gérer l’emploi du temps" onClick={() => openView(VIEWS.timetable)} disabled={!isGerant} />
        <HubCard icon={MessageSquareText} title="Demandes & communication" description="Lire les demandes des enseignants, répondre et suivre leur traitement." action="Ouvrir les demandes" onClick={() => openView(VIEWS.requests)} disabled={!isGerant} />
        <HubCard icon={FileSignature} title="Contrats enseignants" description="Créer les CDI et enregistrer séparément les primes trimestrielles." action="Gérer les contrats" onClick={() => openView(VIEWS.contracts)} disabled={!isGerant} />
      </section>
    </>
  );
}
